import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'react-router-dom';
import { motion as framerMotion } from 'framer-motion';
import { FaArrowLeft, FaRocket, FaGlobe, FaUsers, FaChartLine, FaQuoteLeft, FaCheckCircle } from 'react-icons/fa';
import SEO from '../components/SEO/SEO';

const InvestorInsightDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Mock data matching the main list
  const startupsData = {
    'perplexity-ai': {
      name: "Perplexity AI",
      sector: "AI/Search",
      location: "San Francisco, CA",
      metric: "200+ employees",
      why: "Real-time LLM search; 5,000% growth in daily active users throughout 2025.",
      longDesc: "Perplexity AI has redefined the search experience by moving away from link-based results to direct, conversational answers powered by advanced Large Language Models. Their approach minimizes cognitive load for users and provides sourced, reliable information in real-time.",
      keyStats: ["$1B+ Valuation", "Disrupting Traditional Search", "LLM-First Architecture"],
      impact: "Reducing research time by up to 70% for professionals and students alike."
    },
    'suno': {
      name: "Suno",
      sector: "AI/Music",
      location: "Cambridge, MA",
      metric: "$125M Series B",
      why: "Democratizing music creation with text-to-song generation.",
      longDesc: "Suno is a generative AI company that enables anyone to create full-length, high-quality songs with lyrics, vocals, and instrumentation from a simple text prompt. It leverages custom audio models to push the boundaries of creative expression.",
      keyStats: ["Millions of tracks generated", "Series B led by top-tier VCs", "Industry-leading audio fidelity"],
      impact: "Empowering creators who lack traditional musical training to produce professional-grade audio."
    },
    'charlie-health': {
      name: "Charlie Health",
      sector: "Healthtech",
      location: "Bozeman, MT",
      metric: "2,200 employees",
      why: "Virtual high-acuity mental healthcare for teens and young adults.",
      longDesc: "Charlie Health provides personalized, intensive outpatient treatment (IOP) for those struggling with mental health challenges. Their virtual-first model ensures that high-quality care is accessible regardless of geographic location.",
      keyStats: ["Rapid workforce expansion", "Proven clinical outcomes", "Nationwide coverage in US"],
      impact: "Reducing ER visits and improving long-term recovery rates for youth mental health."
    },
    // Adding more common ones from the list...
    'cohere': {
      name: "Cohere",
      sector: "AI/Enterprise",
      location: "Toronto, ON",
      metric: "$940M Series D",
      why: "Enterprise-grade AI customization and data privacy focus.",
      longDesc: "Cohere provides Large Language Models that enterprises can integrate into their existing stacks with a focus on ease of use and data sovereignty. They allow businesses to leverage AI without compromising sensitive internal information.",
      keyStats: ["Multi-cloud flexibility", "Strategic cloud partnerships", "Global talent base"],
      impact: "Enabling Fortune 500 companies to securely deploy AI at scale."
    }
  };

  const startup = startupsData[id] || {
    name: id.replace(/-/g, ' ').toUpperCase(),
    sector: "Technology Innovation",
    location: "Global Presence",
    why: "A trailblazing startup pushing the boundaries of innovation in 2025.",
    longDesc: "This startup is currently part of our 2025 Aggregated Watchlist. They have shown exceptional growth and market potential within their respective sector, leveraging cutting-edge technology to solve complex problems.",
    keyStats: ["Venture Backed", "High Growth Potential", "Innovative Solution"],
    impact: "Driving digital transformation and industry evolution."
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      <SEO 
        title={`${startup.name} - Investor Insights | Dhyon Technologies`}
        description={startup.longDesc}
      />

      {/* Detail Header */}
      <section className="bg-white border-b border-gray-200 pt-32 pb-16">
        <div className="container mx-auto px-6 max-w-5xl">
          <Link to="/investor-insights" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8 transition-colors font-medium">
            <FaArrowLeft className="mr-2" />
            Back to All Insights
          </Link>
          
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <framerMotion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-blue-100 text-blue-700 px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider">
                  {startup.sector}
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600 flex items-center gap-2">
                  <FaGlobe className="text-xs" />
                  {startup.location}
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight">
                {startup.name}
              </h1>
            </framerMotion.div>

            <framerMotion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-blue-600 to-purple-600 p-1 rounded-2xl shadow-xl"
            >
              <div className="bg-white px-8 py-6 rounded-[14px]">
                <p className="text-gray-500 text-sm font-bold uppercase mb-1">Growth Metric</p>
                <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  {startup.metric || 'Top 1% Growth'}
                </p>
              </div>
            </framerMotion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-6 max-w-5xl mt-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview */}
            <framerMotion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-gray-100"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <FaRocket className="text-blue-600" />
                Strategic Overview
              </h3>
              <p className="text-xl text-gray-700 leading-relaxed mb-8 font-medium">
                {startup.why}
              </p>
              <div className="prose prose-lg text-gray-600 leading-relaxed">
                <p>{startup.longDesc}</p>
              </div>
            </framerMotion.section>

            {/* Impact */}
            <framerMotion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-blue-900 text-white p-8 md:p-12 rounded-3xl shadow-xl relative overflow-hidden"
            >
              <FaQuoteLeft className="absolute top-8 right-8 text-white/10 text-6xl" />
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-3 relative z-10">
                <FaChartLine className="text-blue-400" />
                Investment Impact
              </h3>
              <p className="text-2xl md:text-3xl font-light leading-snug relative z-10 italic">
                "{startup.impact}"
              </p>
            </framerMotion.section>
          </div>

          {/* Right Column (Stats) */}
          <div className="space-y-8">
            <framerMotion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-6">Key Insights</h4>
              <ul className="space-y-4">
                {startup.keyStats.map((stat, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{stat}</span>
                  </li>
                ))}
              </ul>
            </framerMotion.div>

            <div className="bg-gradient-to-br from-gray-900 to-blue-900 p-8 rounded-3xl text-white">
              <h4 className="text-lg font-bold mb-4">Want More Data?</h4>
              <p className="text-blue-100 text-sm mb-6">Access our full 2025 VC report and startup benchmarks.</p>
              <Link to="/contact" className="block text-center py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-bold transition-all">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorInsightDetail;
